import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import api from '../lib/axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  Plus, Search, CheckCircle2, Clock, AlertTriangle, Loader2,
  Phone, Mail, Users, MapPin, FileText, Repeat, MoreHorizontal,
  Calendar, Edit, Trash2, Target, X, CircleDot, Filter, ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import { HelpTooltip, TitleWithHelp } from '../components/ui/help-tooltip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` } });

const TIPOS = [
  { id: 'follow_up', nome: 'Follow-up', icon: Repeat },
  { id: 'ligacao', nome: 'Ligação', icon: Phone },
  { id: 'email', nome: 'E-mail', icon: Mail },
  { id: 'reuniao', nome: 'Reunião', icon: Users },
  { id: 'visita', nome: 'Visita', icon: MapPin },
  { id: 'enviar_proposta', nome: 'Enviar Proposta', icon: FileText },
  { id: 'outro', nome: 'Outro', icon: MoreHorizontal },
];

const PRIORIDADES = [
  { id: 'baixa', nome: 'Baixa', cor: 'bg-slate-100 text-slate-600 border-slate-200' },
  { id: 'media', nome: 'Média', cor: 'bg-blue-100 text-blue-600 border-blue-200' },
  { id: 'alta', nome: 'Alta', cor: 'bg-orange-100 text-orange-600 border-orange-200' },
  { id: 'urgente', nome: 'Urgente', cor: 'bg-red-100 text-red-600 border-red-200' },
];

const STATUS = [
  { id: 'pendente', nome: 'Pendente', cor: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock },
  { id: 'em_andamento', nome: 'Em Andamento', cor: 'bg-blue-100 text-blue-700 border-blue-200', icon: CircleDot },
  { id: 'concluida', nome: 'Concluída', cor: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2 },
  { id: 'cancelada', nome: 'Cancelada', cor: 'bg-slate-100 text-slate-500 border-slate-200', icon: X },
];

const getConfig = (list, id) => list.find(i => i.id === id) || list[0];
const fmtDate = (d) => { if (!d) return '-'; try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return '-'; } };
const isOverdue = (t) => t.data_vencimento && new Date(t.data_vencimento) < new Date(new Date().toDateString()) && !['concluida', 'cancelada'].includes(t.status);

export default function CrmTarefas() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tarefas, setTarefas] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState('ativas'); // ativas | todos | pendente | em_andamento | concluida
  const [filtroPrioridade, setFiltroPrioridade] = useState('todos');
  const [busca, setBusca] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [oportunidades, setOportunidades] = useState([]);

  const defaultForm = () => ({
    titulo: '', descricao: '', tipo: 'follow_up', prioridade: 'media',
    data_vencimento: '', responsavel_id: '', responsavel_nome: '',
    oportunidade_id: '', oportunidade_titulo: '', cliente_id: '', cliente_nome: '',
  });
  const [form, setForm] = useState(defaultForm());

  // ============ FETCH ============
  const fetchTarefas = useCallback(async () => {
    try {
      setLoading(true);
      const [tarefasRes, statsRes] = await Promise.all([
        api.get('/crm-tarefas'),
        api.get('/crm-tarefas/stats'),
      ]);
      setTarefas(tarefasRes.data);
      setStats(statsRes.data);
    } catch { toast.error('Erro ao carregar tarefas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchTarefas();
    api.get('/crm-oportunidades')
      .then(r => setOportunidades(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, [fetchTarefas]);

  // Auto-open from Pipeline
  useEffect(() => {
    const opId = searchParams.get('oportunidade');
    if (opId && !modalOpen) {
      openNew({
        oportunidade_id: opId,
        oportunidade_titulo: searchParams.get('titulo') || '',
        cliente_nome: searchParams.get('cliente') || '',
      });
      setSearchParams({});
    }
  }, [searchParams]); // eslint-disable-line

  // ============ MODAL ============
  const openNew = (presets = {}) => {
    setEditing(null);
    setForm({ ...defaultForm(), ...presets });
    setModalOpen(true);
  };

  const openEdit = (tarefa) => {
    setEditing(tarefa);
    setForm({
      titulo: tarefa.titulo || '', descricao: tarefa.descricao || '',
      tipo: tarefa.tipo || 'follow_up', prioridade: tarefa.prioridade || 'media',
      data_vencimento: tarefa.data_vencimento ? tarefa.data_vencimento.slice(0, 10) : '',
      responsavel_id: tarefa.responsavel_id || '', responsavel_nome: tarefa.responsavel_nome || '',
      oportunidade_id: tarefa.oportunidade_id || '', oportunidade_titulo: tarefa.oportunidade_titulo || '',
      cliente_id: tarefa.cliente_id || '', cliente_nome: tarefa.cliente_nome || '',
    });
    setModalOpen(true);
  };

  // ============ SAVE ============
  const handleSave = async () => {
    if (!form.titulo.trim()) { toast.error('Informe o título da tarefa'); return; }
    try {
      setSaving(true);
      const payload = { ...form, data_vencimento: form.data_vencimento || null };
      if (editing) {
        await api.put(`/crm-tarefas/${editing.id}`, payload);
        toast.success('Tarefa atualizada');
      } else {
        await api.post('/crm-tarefas', payload);
        toast.success('Tarefa criada');
      }
      setModalOpen(false);
      fetchTarefas();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const toggleConcluir = async (tarefa) => {
    try {
      if (tarefa.status === 'concluida') {
        await api.put(`/crm-tarefas/${tarefa.id}`, { status: 'pendente' });
        toast.success('Tarefa reaberta');
      } else {
        await api.put(`/crm-tarefas/${tarefa.id}/concluir`, {});
        toast.success('Tarefa concluída');
      }
      fetchTarefas();
    } catch { toast.error('Erro ao atualizar'); }
  };

  const changeStatus = async (tarefa, novoStatus) => {
    try {
      await api.put(`/crm-tarefas/${tarefa.id}`, { status: novoStatus });
      toast.success(`Status: ${getConfig(STATUS, novoStatus).nome}`);
      fetchTarefas();
    } catch { toast.error('Erro ao atualizar status'); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/crm-tarefas/${deleteConfirm.id}`);
      toast.success('Tarefa excluída');
      setDeleteConfirm(null);
      fetchTarefas();
    } catch { toast.error('Erro ao excluir'); }
  };

  const vincularOportunidade = (opId) => {
    const op = oportunidades.find(o => o.id === opId);
    if (op) setForm(f => ({ ...f, oportunidade_id: op.id, oportunidade_titulo: op.titulo, cliente_nome: op.cliente_nome || op.contato_nome || f.cliente_nome }));
  };

  // ============ FILTER ============
  const filtered = tarefas.filter(t => {
    if (filtroStatus === 'ativas' && ['concluida', 'cancelada'].includes(t.status)) return false;
    if (!['todos', 'ativas'].includes(filtroStatus) && t.status !== filtroStatus) return false;
    if (filtroPrioridade !== 'todos' && t.prioridade !== filtroPrioridade) return false;
    if (busca) {
      const q = busca.toLowerCase();
      return (t.titulo || '').toLowerCase().includes(q) || (t.cliente_nome || '').toLowerCase().includes(q) || (t.oportunidade_titulo || '').toLowerCase().includes(q);
    }
    return true;
  });

  // ============ RENDER ============
  return (
    <div data-testid="tarefas-page" className="space-y-5 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <TitleWithHelp
            title="Tarefas & Follow-up"
            description="Organize e acompanhe todas as tarefas do CRM: follow-ups, ligações, e-mails, reuniões e visitas. Controle prazos e prioridades da equipe comercial."
            connections={['CRM', 'Oportunidades', 'Clientes']}
          >
            <h1 className="text-2xl font-bold text-slate-800">Tarefas & Follow-up</h1>
          </TitleWithHelp>
          <p className="text-sm text-slate-500">Gerencie atividades e acompanhamentos do CRM</p>
        </div>
        <Button data-testid="new-tarefa-btn" onClick={() => openNew()} className="gap-2 bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4" /> Nova Tarefa
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Pendentes', value: stats.pendentes || 0, icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50', help: 'Tarefas que ainda não foram iniciadas.' },
          { label: 'Em Andamento', value: stats.em_andamento || 0, icon: CircleDot, color: 'text-blue-600', bg: 'bg-blue-50', help: 'Tarefas atualmente em execução.' },
          { label: 'Concluídas', value: stats.concluidas || 0, icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50', help: 'Tarefas finalizadas com sucesso.' },
          { label: 'Vencidas', value: stats.vencidas || 0, icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', help: 'Tarefas que passaram da data de vencimento sem serem concluídas.' },
          { label: 'Total', value: stats.total || 0, icon: Target, color: 'text-slate-600', bg: 'bg-slate-50', help: 'Número total de tarefas cadastradas no sistema.' },
        ].map((s, i) => (
          <Card key={i} className={`${s.bg} border-0`}>
            <CardContent className="p-3 flex items-center gap-3">
              <s.icon className={`w-5 h-5 ${s.color}`} />
              <div className="flex-1">
                <div className="flex items-center gap-1">
                  <p className="text-xs text-slate-500">{s.label}</p>
                  <HelpTooltip description={s.help} iconSize={12} />
                </div>
                <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <Input data-testid="search-tarefas" placeholder="Buscar tarefa, cliente ou oportunidade..." value={busca}
            onChange={e => setBusca(e.target.value)} className="pl-9 h-9" />
        </div>
        <Select value={filtroStatus} onValueChange={setFiltroStatus}>
          <SelectTrigger data-testid="filter-status" className="w-36 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ativas">Ativas</SelectItem>
            <SelectItem value="todos">Todas</SelectItem>
            <SelectItem value="pendente">Pendentes</SelectItem>
            <SelectItem value="em_andamento">Em Andamento</SelectItem>
            <SelectItem value="concluida">Concluídas</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filtroPrioridade} onValueChange={setFiltroPrioridade}>
          <SelectTrigger data-testid="filter-prioridade" className="w-36 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Prioridade</SelectItem>
            <SelectItem value="urgente">Urgente</SelectItem>
            <SelectItem value="alta">Alta</SelectItem>
            <SelectItem value="media">Média</SelectItem>
            <SelectItem value="baixa">Baixa</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Task List */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-16 text-slate-500">
            <CheckCircle2 className="w-12 h-12 mb-3 text-slate-300" />
            <p className="font-medium">Nenhuma tarefa encontrada</p>
            <p className="text-sm mt-1">Crie tarefas para organizar seus follow-ups</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(tarefa => {
            const tipoCfg = getConfig(TIPOS, tarefa.tipo);
            const prioCfg = getConfig(PRIORIDADES, tarefa.prioridade);
            const statusCfg = getConfig(STATUS, tarefa.status);
            const overdue = isOverdue(tarefa);
            const TipoIcon = tipoCfg.icon;
            const done = tarefa.status === 'concluida';

            return (
              <div key={tarefa.id} data-testid={`tarefa-item-${tarefa.id}`}
                className={`border rounded-lg p-3 transition-all ${overdue ? 'border-red-300 bg-red-50/30' : done ? 'border-slate-200 bg-slate-50/50 opacity-70' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                <div className="flex items-start gap-3">
                  {/* Checkbox */}
                  <div className="pt-0.5">
                    <Checkbox
                      data-testid={`check-tarefa-${tarefa.id}`}
                      checked={done}
                      onCheckedChange={() => toggleConcluir(tarefa)}
                      className={done ? 'border-green-500 bg-green-500' : ''}
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className={`text-sm font-medium ${done ? 'line-through text-slate-400' : 'text-slate-800'}`}>{tarefa.titulo}</span>
                      <Badge variant="outline" className={`text-[10px] py-0 ${prioCfg.cor}`}>{prioCfg.nome}</Badge>
                      <Badge variant="outline" className={`text-[10px] py-0 ${statusCfg.cor}`}>{statusCfg.nome}</Badge>
                      {overdue && <Badge variant="outline" className="text-[10px] py-0 bg-red-100 text-red-600 border-red-200">Vencida</Badge>}
                    </div>
                    {tarefa.descricao && <p className="text-xs text-slate-500 mb-1 line-clamp-1">{tarefa.descricao}</p>}
                    <div className="flex items-center gap-3 flex-wrap text-[10px] text-slate-400">
                      <span className="flex items-center gap-1"><TipoIcon className="w-3 h-3" />{tipoCfg.nome}</span>
                      {tarefa.data_vencimento && (
                        <span className={`flex items-center gap-1 ${overdue ? 'text-red-500 font-medium' : ''}`}>
                          <Calendar className="w-3 h-3" />{fmtDate(tarefa.data_vencimento)}
                        </span>
                      )}
                      {tarefa.responsavel_nome && <span>{tarefa.responsavel_nome}</span>}
                      {tarefa.oportunidade_titulo && (
                        <span className="flex items-center gap-1 text-blue-500"><Target className="w-3 h-3" />{tarefa.oportunidade_titulo}</span>
                      )}
                      {tarefa.cliente_nome && <span>{tarefa.cliente_nome}</span>}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    {!done && tarefa.status === 'pendente' && (
                      <Button variant="ghost" size="sm" className="h-7 text-[10px] px-2 text-blue-600" onClick={() => changeStatus(tarefa, 'em_andamento')}>Iniciar</Button>
                    )}
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(tarefa)} title="Editar">
                      <Edit className="w-3.5 h-3.5 text-slate-400" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setDeleteConfirm(tarefa)} title="Excluir">
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ============ CREATE/EDIT MODAL ============ */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-xl w-[95vw]">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Tarefa' : 'Nova Tarefa'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <div className="flex items-center gap-1">
                <Label className="text-xs">Título *</Label>
                <HelpTooltip description="Descreva brevemente a ação a ser realizada. Ex: 'Ligar para cliente sobre proposta'." iconSize={12} />
              </div>
              <Input data-testid="input-titulo-tarefa" value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))} placeholder="Ex: Ligar para cliente sobre proposta" className="h-9" />
            </div>
            <div>
              <Label className="text-xs">Descrição</Label>
              <Textarea value={form.descricao} onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))} rows={2} placeholder="Detalhes da tarefa..." className="text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center gap-1">
                  <Label className="text-xs">Tipo</Label>
                  <HelpTooltip description="Classifique a tarefa: Follow-up, Ligação, E-mail, Reunião, Visita ou Enviar Proposta." iconSize={12} />
                </div>
                <Select value={form.tipo} onValueChange={v => setForm(f => ({ ...f, tipo: v }))}>
                  <SelectTrigger data-testid="select-tipo-tarefa" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS.map(t => <SelectItem key={t.id} value={t.id}><div className="flex items-center gap-2"><t.icon className="w-3.5 h-3.5" />{t.nome}</div></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Prioridade</Label>
                <Select value={form.prioridade} onValueChange={v => setForm(f => ({ ...f, prioridade: v }))}>
                  <SelectTrigger data-testid="select-prioridade-tarefa" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORIDADES.map(p => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Data de Vencimento</Label>
                <Input type="date" value={form.data_vencimento} onChange={e => setForm(f => ({ ...f, data_vencimento: e.target.value }))} className="h-9" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Vincular à Oportunidade</Label>
              <Select value={form.oportunidade_id || 'none'} onValueChange={v => { if (v === 'none') setForm(f => ({ ...f, oportunidade_id: '', oportunidade_titulo: '' })); else vincularOportunidade(v); }}>
                <SelectTrigger className="h-9 truncate"><SelectValue placeholder="Nenhuma" /></SelectTrigger>
                <SelectContent className="max-w-[500px]">
                  <SelectItem value="none">Nenhuma</SelectItem>
                  {oportunidades.map(op => <SelectItem key={op.id} value={op.id}><span className="truncate block max-w-[450px]">{op.titulo}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.oportunidade_titulo && (
              <div className="text-xs bg-blue-50 border border-blue-200 rounded px-2 py-1 text-blue-700 truncate">
                <Target className="w-3 h-3 inline mr-1" />{form.oportunidade_titulo}
                {form.cliente_nome && <span className="ml-2 text-blue-500">({form.cliente_nome})</span>}
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button data-testid="save-tarefa-btn" onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {editing ? 'Atualizar' : 'Criar Tarefa'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ DELETE CONFIRM ============ */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir tarefa?</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza que deseja excluir "{deleteConfirm?.titulo}"?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
